import {
    BasePicker,
    getGlobalClassNames,
    getInputFocusStyle,
    IBasePickerProps,
    IBasePickerStyleProps,
    IBasePickerStyles,
    IBasePickerSuggestionsProps,
    styled,
} from '@fluentui/react';
import { IAutocompletePickerOption } from '../../data-types/autocomplete/IAutocompletePickerOption';
import { getRenderSelectedAutocompleteItemHandler } from './autocompleteHelpers';
import { SuggestedAutocompleteItem } from './SuggestedAutocompleteItem';

export interface IBaseAutocompletePickerProps extends IBasePickerProps<IAutocompletePickerOption> {}

class BaseAutocompletePickerBase extends BasePicker<IAutocompletePickerOption, IBaseAutocompletePickerProps> {
    public static defaultProps = {
        onRenderItem: getRenderSelectedAutocompleteItemHandler({ height: 30 }),
        onRenderSuggestionsItem: (item: IAutocompletePickerOption, suggestionsProps?: IBasePickerSuggestionsProps) => {
            return <SuggestedAutocompleteItem {...suggestionsProps} item={item} />;
        },
    };
}

export const BaseAutocompletePicker = styled<IBaseAutocompletePickerProps, IBasePickerStyleProps, IBasePickerStyles>(
    BaseAutocompletePickerBase,
    getStyles,
    undefined,
    {
        scope: 'BaseAutocompletePicker',
    }
);

//stolen from - https://github.com/microsoft/fluentui/blob/master/packages/react/src/components/pickers/BasePicker.styles.ts
// related issue - https://github.com/microsoft/fluentui/issues/21822
const GlobalClassNames = {
    root: 'ms-BasePicker',
    text: 'ms-BasePicker-text',
    itemsWrapper: 'ms-BasePicker-itemsWrapper',
    input: 'ms-BasePicker-input',
};

function getStyles(props: IBasePickerStyleProps): IBasePickerStyles {
    const { className, theme, isFocused, inputClassName, disabled } = props;

    if (!theme) {
        throw new Error('theme is undefined or null in base BasePicker getStyles function.');
    }
    const { semanticColors, effects, fonts } = theme;
    const { inputBorder, inputBorderHovered, inputFocusBorderAlt } = semanticColors;

    const classNames = getGlobalClassNames(GlobalClassNames, theme);

    // The following lines are to create a semi-transparent color overlay for the disabled state with designer's approval.
    const disabledOverlayColor = 'rgba(218, 218, 218, 0.29)';
    const HighContrastSelector = '@media screen and (-ms-high-contrast: active), (forced-colors: active)';
    return {
        root: [classNames.root, className],
        text: [
            classNames.text,
            {
                display: 'flex',
                position: 'relative',
                flexWrap: 'wrap',
                alignItems: 'center',
                boxSizing: 'border-box',
                minWidth: 180,
                minHeight: 30,
                border: `1px solid ${inputBorder}`,
                borderRadius: effects.roundedCorner2,
            },
            !isFocused &&
                !disabled && {
                    selectors: {
                        ':hover': {
                            borderColor: inputBorderHovered,
                        },
                    },
                },
            isFocused && !disabled && getInputFocusStyle(inputFocusBorderAlt, effects.roundedCorner2),
            disabled && {
                borderColor: disabledOverlayColor,
                selectors: {
                    ':after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        background: disabledOverlayColor,
                    },
                    [HighContrastSelector]: {
                        borderColor: 'GrayText',
                        selectors: {
                            ':after': {
                                background: 'none',
                            },
                        },
                    },
                },
            },
        ],
        itemsWrapper: [
            classNames.itemsWrapper,
            {
                display: 'flex',
                flexWrap: 'wrap',
                maxWidth: '100%',
            },
        ],
        input: [
            classNames.input,
            fonts.medium,
            {
                height: 30,
                border: 'none',
                flexGrow: 1,
                outline: 'none',
                padding: '0 6px 0',
                alignSelf: 'flex-end',
                borderRadius: effects.roundedCorner2,
                backgroundColor: 'transparent',
                color: semanticColors.inputText,
                selectors: {
                    '::-ms-clear': {
                        display: 'none',
                    },
                },
            },
            inputClassName,
        ],
        screenReaderText: {
            position: 'absolute',
            width: 1,
            height: 1,
            margin: -1,
            padding: 0,
            border: 0,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
        },
    };
}
